import React, { useEffect, useState, useRef, useCallback } from 'react';
import clsx from 'clsx';
import { RotateCcw, FileDown } from 'lucide-react';
import Page from 'pages/Page';
import {
  fetchChanges,
  ChangeItem,
  startChangeValidationAsync,
  fetchChangeValidationStatus,
  fetchPreviousRuns,
  PreviousRunResult,
  fetchPreviousHeathChecks
} from '../api/changevalidation';
import { useAuth } from '@chainlit/react-client';

const POLL_INTERVAL_MS = 20_000;
const MAX_WAIT_MS = 15 * 60_000;

function fmt(dt?: string) {
  if (!dt) return '-';
  const d = new Date(dt);
  if (isNaN(d.getTime())) return dt;
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
}

export default function ChangeValidationPage() {
  const [changes, setChanges] = useState<ChangeItem[]>([]);
  const [loadingChanges, setLoadingChanges] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const [validationStates, setValidationStates] = useState<Record<string, 'IDLE' | 'RUNNING' | 'READY' | 'ERROR'>>({});
  const [validationErrors, setValidationErrors] = useState<Record<string, string | null>>({});
  const [reportText, setReportText] = useState<string | null>(null);
  const [reportUrls, setReportUrls] = useState<string[]>([]);
  const [reportUrlsMap, setReportUrlsMap] = useState<Record<string, string[]>>({});
  const [reportTextMap, setReportTextMap] = useState<Record<string, string | null>>({});
  const [activeReportUrl, setActiveReportUrl] = useState<string | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [previousRuns, setPreviousRuns] = useState<PreviousRunResult[]>([]);
  const [loadingPrevious, setLoadingPrevious] = useState(false);
  const [previousHealthChecks, setPreviousHealthChecks] = useState<PreviousRunResult[]>([]);
  const [loadingHealthChecks, setLoadingHealthChecks] = useState(false);
  const startTimeRef = useRef<number>(0);
  const [reportReady, setReportReady] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoadingChanges(true);
      setError(null);
      try {
        const data = await fetchChanges();
        if (!active) return;
        setChanges(data); // ✅ REMOVE .sort((a, b) => a.id.localeCompare(b.id))
      } catch (e: any) {
        if (active) setError(e?.message || 'Failed to load changes');
      } finally {
        if (active) setLoadingChanges(false);
      }
    })();
    return () => { active = false; };
  }, []);

  // Fetch previous runs when selectedId changes
  useEffect(() => {
    if (!selectedId) {
      setPreviousRuns([]);
      setPreviousHealthChecks([]);
      return;
    }
    setLoadingPrevious(true);
    setLoadingHealthChecks(true);
    fetchPreviousRuns(selectedId)
      .then(results => setPreviousRuns(results))
      .catch(() => setPreviousRuns([]))
      .finally(() => setLoadingPrevious(false));
    // Fetch health checks using configurationClass
    const selectedChangeObj = changes.find(c => c.id === selectedId);
    if (selectedChangeObj?.configurationClass) {
      fetchPreviousHeathChecks(selectedChangeObj.configurationClass)
        .then(results => setPreviousHealthChecks(results))
        .catch(() => setPreviousHealthChecks([]))
        .finally(() => setLoadingHealthChecks(false));
    } else {
      setPreviousHealthChecks([]);
      setLoadingHealthChecks(false);
    }
  }, [selectedId, changes]);

  useEffect(() => {
    const stored = localStorage.getItem('validationStates');
    if (stored) {
      try {
        setValidationStates(JSON.parse(stored));
      } catch {}
    }
  }, []);

  useEffect(() => {
    const storedUrls = localStorage.getItem('reportUrlsMap');
    if (storedUrls) {
      try {
        setReportUrlsMap(JSON.parse(storedUrls));
      } catch {}
    }
    const storedText = localStorage.getItem('reportTextMap');
    if (storedText) {
      try {
        setReportTextMap(JSON.parse(storedText));
      } catch {}
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('reportUrlsMap', JSON.stringify(reportUrlsMap));
  }, [reportUrlsMap]);

  useEffect(() => {
    localStorage.setItem('reportTextMap', JSON.stringify(reportTextMap));
  }, [reportTextMap]);

  const filteredChanges = changes
    .filter(c =>
      c.id.toLowerCase().includes(filter.toLowerCase()) ||
      c.configurationClass.toLowerCase().includes(filter.toLowerCase())
    )
    .sort((a, b) => {
      // Compare only the DATE part of planned start (ignore time)
      const startDateA = new Date(a.plannedStart).setHours(0, 0, 0, 0);
      const startDateB = new Date(b.plannedStart).setHours(0, 0, 0, 0);
      
      if (startDateA !== startDateB) return startDateA - startDateB;
      
      // If start DATES are the same, sort by planned end (full timestamp)
      const endA = new Date(a.plannedEnd).getTime();
      const endB = new Date(b.plannedEnd).getTime();
      return endA - endB;
    });

  const { user, logout } = useAuth();
  const userEmail = (user?.metadata as any)?.email || '';
  const userRole = (user?.metadata as any)?.role || ''; // NEW: capture role

  const handleSelectChange = useCallback((changeId: string) => {
    setSelectedId(changeId);
    // setValidationStates(prev => ({ ...prev, [changeId]: 'IDLE' }));
    setValidationErrors(prev => ({ ...prev, [changeId]: null }));
    setReportText(null);
    setReportUrls([]);
    setActiveReportUrl(null);
    setPdfUrl(null);
  }, []);

  const beginValidation = useCallback(async () => {
    if (!selectedId) return;

    // If there is already a report, refresh previous runs and health checks
    if ((reportUrlsMap[selectedId]?.length > 0) || reportTextMap[selectedId]) {
         // Clear current report for this change
    setReportUrlsMap(prev => ({ ...prev, [selectedId]: [] }));
    setReportTextMap(prev => ({ ...prev, [selectedId]: null }));
        
        
      setLoadingPrevious(true);
      setLoadingHealthChecks(true);
      fetchPreviousRuns(selectedId)
        .then(results => setPreviousRuns(results))
        .catch(() => setPreviousRuns([]))
        .finally(() => setLoadingPrevious(false));
      const selectedChangeObj = changes.find(c => c.id === selectedId);
      if (selectedChangeObj?.configurationClass) {
        fetchPreviousHeathChecks(selectedChangeObj.configurationClass)
          .then(results => setPreviousHealthChecks(results))
          .catch(() => setPreviousHealthChecks([]))
          .finally(() => setLoadingHealthChecks(false));
      } else {
        setPreviousHealthChecks([]);
        setLoadingHealthChecks(false);
      }
    }

    setValidationStates(prev => ({ ...prev, [selectedId]: 'RUNNING' }));
    setValidationErrors(prev => ({ ...prev, [selectedId]: null }));
    setReportText(null);
    setReportUrls([]);
    setActiveReportUrl(null);
    setPdfUrl(null);
    startTimeRef.current = Date.now();
    try {
      const startResp = await startChangeValidationAsync(selectedId, userEmail);
      const taskId = startResp.task_id;
      const poll = async () => {
        try {
          const status = await fetchChangeValidationStatus(taskId);
          if (status.status === 'error') {
            setValidationStates(prev => ({ ...prev, [selectedId]: 'ERROR' }));
            setValidationErrors(prev => ({ ...prev, [selectedId]: status.error || 'Validation failed.' }));
            return;
          }
          if (status.status === 'finished') {
            setValidationStates(prev => ({ ...prev, [selectedId]: 'READY' }));
            setReportUrlsMap(prev => ({
              ...prev,
              [selectedId]: Array.isArray(status.report_url)
                ? status.report_url
                : (status.report_url && String(status.report_url).startsWith('['))
                  ? JSON.parse(status.report_url)
                  : status.report_url
                    ? [status.report_url]
                    : []
            }));
            setReportTextMap(prev => ({
              ...prev,
              [selectedId]: status.text || null
            }));
            setReportReady(true); // <-- Add this line
            return;
          }
          if (Date.now() - startTimeRef.current > MAX_WAIT_MS) {
            setValidationStates(prev => ({ ...prev, [selectedId]: 'ERROR' }));
            setValidationErrors(prev => ({ ...prev, [selectedId]: 'Validation timed out.' }));
            return;
          }
          setTimeout(poll, POLL_INTERVAL_MS);
        } catch (e: any) {
          setValidationStates(prev => ({ ...prev, [selectedId]: 'ERROR' }));
          setValidationErrors(prev => ({ ...prev, [selectedId]: e?.message || 'Status polling failed.' }));
        }
      };
      poll();
    } catch (e: any) {
      setValidationStates(prev => ({ ...prev, [selectedId]: 'ERROR' }));
      setValidationErrors(prev => ({ ...prev, [selectedId]: e?.message || 'Failed to start validation.' }));
    }
  }, [selectedId, changes, reportUrlsMap, reportTextMap]);

  useEffect(() => {
    return () => {
      if (pdfUrl && pdfUrl.startsWith('blob:')) URL.revokeObjectURL(pdfUrl);
    };
  }, [pdfUrl]);

  useEffect(() => {
    localStorage.setItem('validationStates', JSON.stringify(validationStates));
  }, [validationStates]);

  const selectedChange = selectedId ? changes.find(c => c.id === selectedId) : null;

  const currentValidationState = selectedId ? validationStates[selectedId] || 'IDLE' : 'IDLE';
  const currentValidationError = selectedId ? validationErrors[selectedId] : null;

  const currentReportUrls = selectedId ? reportUrlsMap[selectedId] || [] : [];
  const currentReportText = selectedId ? reportTextMap[selectedId] || null : null;

  return (
    <Page>
      <div className="flex flex-col h-full w-full p-3 gap-3">
        <div className="flex flex-1 min-h-0 gap-4">
          {/* Left list */}
          <section
            className="flex flex-col bg-accent dark:bg-card border border-border/50 rounded-2xl p-3 min-h-0 shrink-0"
            style={{ width: 300 }}
          >
            <div className="mb-2 flex items-center justify-between">
              <h2 className="text-sm font-semibold tracking-wide uppercase text-muted-foreground">
                Changes
              </h2>
              <button
                onClick={() => {
                  setLoadingChanges(true);
                  fetchChanges()
                    .then(data => setChanges(data)) // ✅ REMOVE the .sort() here
                    .catch(e => setError(e?.message || 'Refresh failed'))
                    .finally(() => setLoadingChanges(false));
                }}
                disabled={loadingChanges}
                className="text-[10px] px-2 py-1 rounded-md border border-border/50 hover:bg-[#18E78F]/15 disabled:opacity-40"
                title="Refresh"
              >
                <RotateCcw className={`w-3.5 h-3.5 ${loadingChanges ? 'animate-spin' : ''}`} />
              </button>
            </div>
            <input
              placeholder="Filter…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="mb-3 text-xs rounded-md px-2 py-1 bg-background/60 dark:bg-background/40 border border-border/50 focus:outline-none focus:ring-1 focus:ring-[#18E78F]/50"
            />
            <div className="overflow-y-auto overflow-y-auto custom-scrollbar incidents-scroll space-y-2 pr-1 incidents-scroll space-y-2 pr-1">
              {error && (
                <div className="text-[11px] text-red-500 border border-red-500/40 bg-red-500/10 rounded-md px-2 py-1">
                  {error}
                </div>
              )}
              {filteredChanges.map(c => {
                const active = c.id === selectedId;
                const isRunning = validationStates[c.id] === 'RUNNING';
                return (
                  <button
                    key={c.id}
                    onClick={() => handleSelectChange(c.id)}
                    className={clsx(
                      'w-full text-left group rounded-xl px-3 py-2 transition-colors border',
                      active
                        ? 'bg-[#18E78F]/15 border-[#18E78F]/50'
                        : 'bg-background/50 dark:bg-background/30 border-border/40 hover:bg-[#18E78F]/10'
                    )}
                  >
                    <div className="flex items-center justify-between">
                      <span className={clsx('text-xs font-semibold tracking-wide', active && 'text-primary')}>
                        {c.id}
                      </span>
                      <span className="text-[9px] px-1.5 py-0.5 rounded-md bg-background/70 border border-border/40 tabular-nums">
                        {new Date(c.plannedStart).toLocaleDateString()}
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground line-clamp-2">
                      {c.configurationClass}
                    </p>
                    <div className="mt-1 flex flex-col gap-0.5 text-[10px] text-muted-foreground/70">
                      <div className="flex justify-between">
                        <span>Start</span><span className="tabular-nums">{fmt(c.plannedStart)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>End</span><span className="tabular-nums">{fmt(c.plannedEnd)}</span>
                      </div>
                    </div>
                    {/* Show loading animation if this change is running */}
                    {isRunning && (
                      <span className="mt-2 text-[10px] px-2 py-1 rounded-md border border-[#18E78F]/40 bg-[#18E78F]/10 text-[#18E78F] flex items-center gap-1">
                        <span className="animate-spin inline-block w-3 h-3 border-2 border-[#18E78F]/30 border-t-[#18E78F] rounded-full" />
                        Running…
                      </span>
                    )}
                  </button>
                );
              })}
              {!loadingChanges && filteredChanges.length === 0 && (
                <div className="text-xs text-muted-foreground py-4 text-center">No changes found.</div>
              )}
            </div>
          </section>

          {/* Right details + reports */}
          <section className="flex flex-col bg-accent dark:bg-card border border-border/50 rounded-2xl p-4 min-h-0 flex-1">
            {!selectedChange && (
              <div className="m-auto text-[11px] text-muted-foreground/60">
                Select a change to view details.
              </div>
            )}

            {selectedId && selectedChange && (
              <div className="flex flex-col gap-4 min-h-0 flex-1">
                {/* Change Details Header */}
                <div className="flex items-center justify-between">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Change Details
                  </h3>
                  <div className="flex items-center gap-2">
                    {/* Show button only if dd_enabled is true and not running */}
                    {selectedChange?.dd_enabled ? (
                      currentValidationState !== 'RUNNING' && (
                        <button
                          onClick={beginValidation}
                          className="text-[10px] px-3 py-1 rounded-md border border-border/50 hover:bg-[#18E78F]/15 transition-colors"
                        >
                          Run Change Validation
                        </button>
                      )
                    ) : (
                      <span className="text-[10px] px-2 py-1 rounded-md border border-border/40 bg-background/60 text-muted-foreground">
                        Datadog monitoring for this application is not enabled
                      </span>
                    )}
                    {currentValidationState === 'RUNNING' && (
                      <span className="text-[10px] px-2 py-1 rounded-md border border-[#18E78F]/40 bg-[#18E78F]/10 text-[#18E78F]">
                        Running…
                      </span>
                    )}
                  </div>
                </div>

                {currentValidationState === 'ERROR' && (
                  <div className="text-[11px] text-red-500 border border-red-500/40 bg-red-500/10 rounded-md px-3 py-2">
                    {currentValidationError || 'Unexpected error.'}
                  </div>
                )}

                {/* Stats */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-[13px]">
                  <Stat label="Number" value={selectedChange.id} />
                  <Stat label="Config Class" value={selectedChange.configurationClass || '-'} />
                  <Stat label="Planned Start" value={fmt(selectedChange.plannedStart)} />
                  <Stat label="Planned End" value={fmt(selectedChange.plannedEnd)} />
                  <Stat label="Assigned To" value={selectedChange.assignedTo || '-'} />
                  <Stat label="Assignment Group" value={selectedChange.assignmentGroup || '-'} />
                </div>

                {/* Scrollable text areas */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-[13px]">
                  <ScrollableTextArea
                    label="Short Description"
                    value={selectedChange.shortDescription || '(none)'}
                  />
                  <ScrollableTextArea
                    label="Description"
                    value={selectedChange.description || '(none)'}
                  />
                </div>

                {/* Previous runs, health checks, and reports */}
                <div className="flex flex-row gap-4 w-full flex-1 min-h-0" style={{ minHeight: 0 }}>
                  {/* Previous Validation Runs */}
                  <section className="flex-1 bg-background/60 border border-border/30 rounded-xl p-3 min-h-0 h-full overflow-y-auto min-w-[260px]">
                    <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                      Previous Validation Runs
                    </h4>
                    {loadingPrevious && (
                      <div className="text-[10px] text-muted-foreground/70">Loading previous runs…</div>
                    )}
                    {!loadingPrevious && previousRuns.length === 0 && (
                      <div className="text-[10px] text-muted-foreground/60">No previous runs found.</div>
                    )}
                    {!loadingPrevious && previousRuns.length > 0 && (
                      <div className="flex flex-col gap-2">
                        {previousRuns.map((run, i) => {
                          let urls: string[] = [];
                          try {
                            urls = JSON.parse(run.report_url);
                            if (!Array.isArray(urls)) urls = [];
                          } catch {
                            if (typeof run.report_url === 'string') urls = [run.report_url];
                          }
                          const dateLabel = fmt(run.updated_at) || '-';
                          const isLatest = i === 0;
                          return (
                            <div
                              key={dateLabel + i}
                              className={clsx(
                                "flex flex-col gap-1 rounded-md p-1",
                                isLatest && "bg-[#18E78F]/10"
                              )}
                            >
                              <div className={clsx("text-[10px] font-semibold", isLatest ? "text-[#18E78F]" : "text-muted-foreground/80")}>
                                {dateLabel} CDT {isLatest && <span className="ml-1 text-[9px] font-bold">Latest</span>}
                              </div>
                              <div className="flex flex-wrap gap-1">
                                {urls.map((url, idx) => (
                                  <a
                                    key={url + idx}
                                    href={url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={clsx(
                                      "text-[10px] px-2 py-1 rounded border transition-colors",
                                      isLatest
                                        ? "border-[#18E78F]/60 bg-[#18E78F]/20 font-semibold"
                                        : "border-border/40 bg-background/70 hover:bg-[#18E78F]/10"
                                    )}
                                    title={`Validation Report ${idx + 1} (${dateLabel})`}
                                  >
                                    Report {idx + 1}
                                  </a>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </section>

                  {/* Previous Health Checks */}
                  <section className="flex-1 bg-background/60 border border-border/30 rounded-xl p-3 min-h-0 h-full overflow-y-auto min-w-[260px]">
                    <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                      Previous Health Checks
                    </h4>
                    {loadingHealthChecks && (
                      <div className="text-[10px] text-muted-foreground/70">Loading previous health checks…</div>
                    )}
                    {!loadingHealthChecks && previousHealthChecks.length === 0 && (
                      <div className="text-[10px] text-muted-foreground/60">No previous health checks found.</div>
                    )}
                    {!loadingHealthChecks && previousHealthChecks.length > 0 && (
                      <div className="flex flex-col gap-2">
                        {previousHealthChecks.map((run, i) => {
                          let urls: string[] = [];
                          try {
                            urls = JSON.parse(run.report_url);
                            if (!Array.isArray(urls)) urls = [];
                          } catch {
                            if (typeof run.report_url === 'string') urls = [run.report_url];
                          }
                          const dateLabel = fmt(run.updated_at) || '-';
                          const isLatest = i === 0;
                          return (
                            <div
                              key={dateLabel + i}
                              className={clsx(
                                "flex flex-col gap-1 rounded-md p-1",
                                isLatest && "bg-[#18E78F]/10"
                              )}
                            >
                              <div className={clsx("text-[10px] font-semibold", isLatest ? "text-[#18E78F]" : "text-muted-foreground/80")}>
                                {dateLabel} CDT {isLatest && <span className="ml-1 text-[9px] font-bold">Latest</span>}
                              </div>
                              <div className="flex flex-wrap gap-1">
                                {urls.map((url, idx) => (
                                  <a
                                    key={url + idx}
                                    href={url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={clsx(
                                      "text-[10px] px-2 py-1 rounded border transition-colors",
                                      isLatest
                                        ? "border-[#18E78F]/60 bg-[#18E78F]/20 font-semibold"
                                        : "border-border/40 bg-background/70 hover:bg-[#18E78F]/10"
                                    )}
                                    title={`Health Check ${idx + 1} (${dateLabel})`}
                                  >
                                    Health {idx + 1}
                                  </a>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </section>

                  {/* Current Reports */}
                  <section className="flex-1 bg-background/60 border border-border/30 rounded-xl p-3 min-h-0 h-full overflow-y-auto min-w-[260px]">
                    <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                      Current Reports
                    </h4>
                    {selectedId && validationStates[selectedId] !== 'IDLE' && (
                      <>
                        {(currentReportUrls.length > 0 || currentReportText) && (
                          <div className={clsx(
                            "text-[9px] text-muted-foreground/70 mb-1 px-2 py-1 rounded",
                            "bg-[#18E78F]/10"
                          )}>
                            Last updated: {fmt(new Date().toISOString())}
                          </div>
                        )}
                        {/* Show report text if available */}
                        {currentValidationState === 'READY' && currentReportUrls.length === 0 && currentReportText && (
                          <div className="text-[11px] whitespace-pre-wrap font-mono p-3 rounded-md border border-border/40 bg-background/60 max-h-40 overflow-y-auto mb-2">                        const [reportReady, setReportReady] = useState(false);
                            {currentReportText}
                          </div>
                        )}

                        {/* Show report URLs if available */}
                        {currentReportUrls.length > 0 && (
                          <div className="flex flex-wrap gap-2 mb-2">
                            {currentReportUrls.map((u, i) => {
                              const active = u === activeReportUrl;
                              return (
                                <div key={u} className="flex flex-col gap-1">
                                  <span className="text-[9px] text-muted-foreground/70">
                                    {fmt(new Date().toISOString())}
                                  </span>
                                  <button
                                    onClick={() => {
                                      setActiveReportUrl(u);
                                      setPdfUrl(u);
                                      window.open(u, '_blank', 'noopener,noreferrer');
                                    }}
                                    className={clsx(
                                      'text-[10px] px-3 py-1 rounded-md border transition-colors',
                                      active
                                        ? 'border-[#18E78F]/60 bg-[#18E78F]/15'
                                        : 'border-border/50 hover:bg-[#18E78F]/10'
                                    )}
                                    title={u}
                                  >
                                    Report {i + 1}
                                  </button>
                                </div>
                              );
                            })}
                            {activeReportUrl && activeReportUrl.toLowerCase().includes('.pdf') && (
                              <a
                                href={activeReportUrl}
                                download={`validation-${selectedId}.pdf`}
                                className="text-[10px] px-3 py-1 rounded-md border border-border/50 hover:bg-[#18E78F]/15 transition-colors flex items-center gap-1"
                              >
                                <FileDown className="w-3.5 h-3.5" />
                                Download PDF
                              </a>
                            )}
                          </div>
                        )}

                        {/* Show loading below the report if running */}
                        {currentValidationState === 'RUNNING' && (
                          <>
                            <div className="text-[9px] text-muted-foreground/70 mb-1">
                              {fmt(new Date().toISOString())}
                            </div>
                            <LoadingReports startedAt={startTimeRef.current} />
                          </>
                        )}
                      </>
                    )}
                    {(!selectedId || validationStates[selectedId] === 'IDLE') && (
                      <div className="text-[10px] text-muted-foreground/60">No current reports.</div>
                    )}
                  </section>
                </div>
              </div>
            )}
          </section>
        </div>
      </div>
    </Page>
  );
}

// Reusable stat tile
function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col bg-background/50 dark:bg-background/40 border border-border/40 rounded-lg px-2 py-1.5">
      <span className="text-[9px] uppercase tracking-wide text-muted-foreground">{label}</span>
      <span className="text-[11px] font-semibold truncate">{value}</span>
    </div>
  );
}

// Scrollable fixed-height block
function ScrollableTextArea({ label, value }: { label: string; value: string }) {
    return (
      <div className="flex flex-col bg-background/40 dark:bg-background/30 border border-border/40 rounded-lg p-3">
        <span className="text-[11px] uppercase tracking-wide text-muted-foreground/70 mb-2">{label}</span>
        <div className="text-[13px] whitespace-pre-wrap leading-relaxed max-h-60 min-h-[60px] overflow-y-auto custom-scrollbar incidents-scroll space-y-2 pr-1">
          {value}
        </div>
      </div>
    );
  }

// New small loading component
function LoadingReports({ startedAt }: { startedAt: number }) {
  const [tick, setTick] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const i = setInterval(() => {
      setTick(t => (t + 1) % 4);
      setElapsed(Math.floor((Date.now() - startedAt) / 1000));
    }, 700);
    return () => clearInterval(i);
  }, [startedAt]);
  const dots = '.'.repeat(tick) + ' '.repeat(3 - tick);
  return (
    <div className="flex items-center gap-3 px-3 py-2 rounded-lg border border-border/50 bg-background/60">
      <div className="relative w-5 h-5">
        <div className="absolute inset-0 rounded-full border-2 border-[#18E78F]/30" />
        <div className="absolute inset-0 rounded-full border-t-2 border-[#18E78F] animate-spin" />
      </div>
      <div className="flex flex-col">
        <span className="text-[10px] font-medium text-muted-foreground">
          Generating reports{dots}
        </span>
        <span className="text-[9px] text-muted-foreground/60">
          Elapsed: {elapsed}s • Poll every {(POLL_INTERVAL_MS / 1000)}s
        </span>
      </div>
    </div>
  );
}
